import type { TOC } from '@ember/component/template-only';
import { concat, fn } from '@ember/helper';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { formatDate, formatMessage } from 'ember-intl';
import eq from 'ember-truth-helpers/helpers/eq';
import WrAnchor from 'ember-wiser/components/anchor';
import WrDetails from 'ember-wiser/components/details';
import {
  ArrowSquareOut as ArrowSquareOutIcon,
  File as FileIcon,
  ReadComment as ReadCommentIcon,
  Refresh as RefreshIcon
} from 'ember-wiser/components/icons';
import { type Signature as IconSignature } from 'ember-wiser/components/icons/refresh';
import WrPill from 'ember-wiser/components/pill';
import Timeline from 'ember-wiser/components/timeline';
import AddComment from 'parog-web/components/alerts-and-notifications/comment/add-comment';
import Comment from 'parog-web/components/alerts-and-notifications/comment/display-comment';
import NotificationActionsButton from 'parog-web/components/notification/actions-button';
import type CommentWrittenJournalEvent from 'parog-web/models/comment-written-journal-event';
import type NotificationProcessedJournalEvent from 'parog-web/models/notification-processed-journal-event';
import type NotificationTriggeredJournalEvent from 'parog-web/models/notification-triggered-journal-event';
import alertingJeStyle from 'parog-web/modifiers/alerting-je-style';
import type SemeiaSessionService from 'parog-web/services/session';
import { sortBy } from 'parog-web/utils/sort';

interface Signature {
  Element: HTMLDivElement;

  Args: {
    bioNotifications: NotificationTriggeredJournalEvent[];
    isTransitionEnabled?: boolean;
  };
}

interface NotificationEntrySignature {
  Args: {
    notification: NotificationTriggeredJournalEvent;
  };
}

interface ProcessedNotificationEntrySignature {
  Args: {
    notification: NotificationProcessedJournalEvent;
  };
}
interface BioExamResultLinkSignature {
  Args: {
    patientId: string;
    bioExamResultId: string;
  };
}

interface CountPillSignature {
  Element: HTMLDivElement;
  Args: {
    count: number;
    icon: TOC<IconSignature>;
  };
}

const NotificationTimelineEntry: TOC<NotificationEntrySignature> = <template>
  <div class='text-grey pb-2'>
    {{formatDate @notification.occurredAt format='DD/MM/YYYY'}}
  </div>
  <div
    class='flex flex-row items-start gap-x-3'
    data-test-notification-information={{@notification.id}}
  >
    {{#if (eq @notification.label "Résultats d'examen biologiques")}}
      <FileIcon class='text-grey shrink-0' />

    {{else}}
      <RefreshIcon class='text-grey shrink-0' />
    {{/if}}

    <div
      class='flex w-full justify-between pr-2'
      data-test-notification-descriptions
      {{alertingJeStyle @notification}}
    >
      <div class='text-p1 mb-2 font-sans'>
        {{@notification.label}}
      </div>
    </div>
  </div>
</template>;

const ProcessedNotificationTimelineEntry: TOC<ProcessedNotificationEntrySignature> =
  <template>
    <div
      class='bg-grey-light text-grey -mt-2 rounded p-2'
      data-test-notification-information='processed'
    >
      Traitée
      {{#if (eq @notification.source 'auto:alerts-treated')}}
        automatiquement
      {{/if}}
      le
      {{formatMessage
        '{occurredAt, date} à {occurredAt, time, hhmm}'
        occurredAt=@notification.occurredAt
      }}
      {{#unless (eq @notification.source 'auto:alerts-treated')}}
        par
        {{@notification.creator.fullName}}
      {{/unless}}
    </div>
  </template>;

const BioExamResultLink: TOC<BioExamResultLinkSignature> = <template>
  <WrAnchor
    href={{concat
      '/medics-web/patients/'
      @patientId
      '/bio-results/edition?sourceJeId='
      @bioExamResultId
    }}
  ><div class='text-purple text-p1 flex flex-row items-center gap-1'>
      <ArrowSquareOutIcon />
      Voir
    </div></WrAnchor>
</template>;

const CountPill: TOC<CountPillSignature> = <template>
  <WrPill
    @variant='grey'
    @size='single-line'
    class='!px-3 !py-1 [&>span]:inline-flex [&>span]:items-center [&>span]:gap-0.5'
    ...attributes
  >
    <@icon class='h-4 w-4' />
    {{@count}}
  </WrPill>
</template>;

export default class BioExamNotifs extends Component<Signature> {
  @service declare session: SemeiaSessionService;

  @tracked isDetailsOpen = false;
  @tracked shouldShowComment = false;

  get bioSenderDisplayed() {
    return (
      this.mostRecentNotification?.apimailSender &&
      this.session.medic?.bioSenderDisplayed
    );
  }

  get canCommentOnNotifications() {
    return this.session.medic?.enableCommentsOnNotifications;
  }

  get commentsCount() {
    return this.args.bioNotifications
      .flatMap(notif => notif.commentWrittenJournalEvents)
      .filter(comment => !comment.isArchived).length;
  }

  get hasOneAlertingJeStyleNotif(): boolean {
    return this.args.bioNotifications.some(n => n.isAlertingJournalEvent);
  }

  get mostRecentNotification(): NotificationTriggeredJournalEvent {
    return this.sortedNotifications[0];
  }

  get prescriptor() {
    return this.mostRecentNotification?.prescriptor;
  }

  get sortedNotifications(): Array<NotificationTriggeredJournalEvent> {
    return [...this.args.bioNotifications].sort(sortBy('occurredAt', 'desc'));
  }

  get updatesCount() {
    return Math.max(0, this.args.bioNotifications.length - 1);
  }

  activeCommentsSorted(
    notification: NotificationTriggeredJournalEvent
  ): CommentWrittenJournalEvent[] {
    return notification.commentWrittenJournalEvents
      .filter(je => !je.isArchived)
      .sort((a, b) => b.occurredAt.getTime() - a.occurredAt.getTime());
  }

  showEditComment = (
    notification: NotificationTriggeredJournalEvent
  ): boolean => {
    return (
      notification === this.mostRecentNotification && this.shouldShowComment
    );
  };

  <template>
    <div class='flex items-start pr-2' ...attributes>
      <WrDetails
        @open={{this.isDetailsOpen}}
        @color='grey'
        class='w-full pt-2 [&>summary]:mb-0 [&>summary]:bg-transparent [&>summary]:px-0 [&>summary]:pb-0 [&>summary]:!text-black [&[open]]:pb-0'
        data-test-notification
      >
        <:summary>

          <div class='flex w-full justify-between pr-2'>
            <div
              class='text-p1 font-sans
                {{if this.hasOneAlertingJeStyleNotif "alerting-style-class"}}'
            >
              <div class='flex flex-row items-center gap-2'>
                <span class='flex w-full'>Examen biologique</span>
                {{#if @isTransitionEnabled}}
                  <BioExamResultLink
                    @patientId={{this.mostRecentNotification.patient.id}}
                    @bioExamResultId={{this.mostRecentNotification.triggeringJournalEventId}}
                  />
                {{/if}}
                {{#if this.updatesCount}}
                  <CountPill
                    @count={{this.updatesCount}}
                    @icon={{RefreshIcon}}
                    data-test-update-pill
                  />
                {{/if}}
                {{#if this.commentsCount}}
                  <CountPill
                    @count={{this.commentsCount}}
                    @icon={{ReadCommentIcon}}
                    data-test-comment-pill
                  />
                {{/if}}
              </div>
              <div
                class='text-purple-medium pt-1'
                data-test-triggering-journal-event-date
              >
                {{#if this.prescriptor}}
                  Prescrit par
                  {{this.prescriptor}},
                {{/if}}
                Prélevé le
                {{formatDate
                  this.mostRecentNotification.triggeringJournalEventOccurredAt
                  format='DD/MM/YYYY'
                }}
              </div>
              {{#if this.bioSenderDisplayed}}
                <span class='text-grey pt-1'>Reçu via :
                  {{this.mostRecentNotification.apimailSender}}</span>
              {{/if}}
            </div>
          </div>
        </:summary>

        <:default>
          <div class='pl-4'>
            <Timeline as |Entry|>
              {{#each this.sortedNotifications as |notification|}}

                {{#if (this.showEditComment notification)}}
                  <AddComment
                    @onClose={{fn (mut this.shouldShowComment) false}}
                    @item={{notification}}
                    data-test-notif-comment-section
                  />
                {{/if}}

                {{#if notification.commentWrittenJournalEvents}}
                  {{#each
                    (this.activeCommentsSorted notification)
                    as |comment|
                  }}
                    <Entry @contentStyles='mb-4'>
                      <Comment
                        @comment={{comment}}
                        class='-mt-2'
                        data-test-notification-information='comment'
                      />
                    </Entry>
                  {{/each}}
                {{/if}}

                <Entry @dotColor='purple-medium'>
                  <NotificationTimelineEntry @notification={{notification}} />
                </Entry>

                {{#if notification.notificationProcessedJournalEvent}}
                  <Entry @contentStyles='mb-4'>
                    <ProcessedNotificationTimelineEntry
                      @notification={{notification.notificationProcessedJournalEvent}}
                    />
                  </Entry>
                {{/if}}

              {{/each}}
            </Timeline>
          </div>
        </:default>
      </WrDetails>

      {{#if this.canCommentOnNotifications}}
        <div class='pt-3'>
          <NotificationActionsButton
            @notification={{this.mostRecentNotification}}
            @showComment={{fn (mut this.shouldShowComment) true}}
            @openDetails={{fn (mut this.isDetailsOpen) true}}
          />
        </div>
      {{/if}}

    </div>
  </template>
}
